export type ProgramSeason = 'winter' | 'spring' | 'summer' | 'fall';

/**
 * Stable identifier for a school program term (used for squads + billing).
 *
 * Format: `${season}${year}` e.g. "winter2026", "spring2026".
 *
 * Note: This is intentionally NOT tied to AvailabilityQuarterKey (teacher availability),
 * which is a separate concept and may cover different date windows.
 */
export type ProgramTermId = string;

export type ProgramTerm = {
  id: ProgramTermId;
  season: ProgramSeason;
  year: number;
  /**
   * Human label used in UI (preferred).
   * Example: "Winter 2026"
   */
  label: string;
  /**
   * Known legacy labels that have existed in the DB/UI.
   * Used for best-effort parsing/migration.
   */
  legacyLabels: string[];
};

const SEASONS: Array<{ season: ProgramSeason; label: string }> = [
  { season: 'winter', label: 'Winter' },
  { season: 'spring', label: 'Spring' },
  { season: 'summer', label: 'Summer' },
  { season: 'fall', label: 'Fall' },
];

export function isProgramTermId(v: unknown): v is ProgramTermId {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return /^(winter|spring|summer|fall)\d{4}$/.test(s);
}

export function programTermId(season: ProgramSeason, year: number): ProgramTermId {
  return `${season}${year}`;
}

export function programTermLabel(season: ProgramSeason, year: number): string {
  const seasonLabel = SEASONS.find(s => s.season === season)?.label || season;
  return `${seasonLabel} ${year}`;
}

export function programTermLabelFromId(id: ProgramTermId | null | undefined): string {
  const term = programTermFromId(id);
  return term?.label || (id ? String(id) : '');
}

export function programTermFromId(id: ProgramTermId | null | undefined): ProgramTerm | null {
  if (!id) return null;
  const s = String(id).trim().toLowerCase();
  const m = s.match(/^(winter|spring|summer|fall)(\d{4})$/);
  if (!m) return null;
  const season = m[1] as ProgramSeason;
  const year = Number(m[2]);
  if (!Number.isFinite(year)) return null;
  return buildProgramTerm(season, year);
}

function normalizeTermLabel(v: string): string {
  return String(v || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .toLowerCase();
}

/**
 * Best-effort mapping from historical/legacy labels into the stable term id.
 * Handles:
 * - "Winter, 2026"
 * - "Winter 2026"
 * - "Winter (Jan–Mar 2026)"
 */
export function programTermIdFromLegacyLabel(label: unknown): ProgramTermId | null {
  const raw = typeof label === 'string' ? label : '';
  const norm = normalizeTermLabel(raw);
  if (!norm) return null;

  // Common: "Winter, 2026" or "Winter 2026"
  for (const s of SEASONS) {
    const seasonWord = s.label.toLowerCase();
    const m = norm.match(new RegExp(`\\b${seasonWord}\\b[^0-9]*(\\d{4})\\b`));
    if (m) {
      const year = Number(m[1]);
      if (Number.isFinite(year)) return programTermId(s.season, year);
    }
  }
  return null;
}

export function resolveProgramTermId(params: {
  termId?: unknown;
  legacyLabel?: unknown;
}): ProgramTermId | null {
  const rawId = typeof params.termId === 'string' ? params.termId.trim() : '';
  if (rawId && isProgramTermId(rawId)) return rawId.toLowerCase();
  return programTermIdFromLegacyLabel(params.legacyLabel);
}

export function listProgramTerms(params?: { startYear?: number; years?: number }): ProgramTerm[] {
  const nowYear = new Date().getFullYear();
  const startYear = Number.isFinite(params?.startYear as any) ? Number(params!.startYear) : nowYear - 1;
  const years = Number.isFinite(params?.years as any) ? Math.max(1, Number(params!.years)) : 6;

  const out: ProgramTerm[] = [];
  for (let y = startYear; y < startYear + years; y++) {
    for (const s of SEASONS) {
      out.push(buildProgramTerm(s.season, y));
    }
  }
  return out;
}

export function programTermForDate(d: Date): ProgramTerm {
  const month = d.getMonth() + 1; // 1-12
  const year = d.getFullYear();
  // Simple quarter mapping for program-term selection defaults.
  // (This is NOT teacher availability quarter logic.)
  const season: ProgramSeason =
    month <= 3 ? 'winter' :
    month <= 6 ? 'spring' :
    month <= 9 ? 'summer' :
    'fall';
  return buildProgramTerm(season, year);
}

function buildProgramTerm(season: ProgramSeason, year: number): ProgramTerm {
  const label = programTermLabel(season, year);
  return {
    id: programTermId(season, year),
    season,
    year,
    label,
    legacyLabels: [
      `${SEASONS.find(s => s.season === season)?.label || season}, ${year}`, // old AdminPanel format
      `${SEASONS.find(s => s.season === season)?.label || season} ${year}`,
    ],
  };
}

